import {
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../../config';
import { groupsRef } from './read';
import { usersRef } from '../user/read';
import { groupLimit } from '../../../../constants/enum';
import { groupMessages } from '../../../../constants/messages';
import { ValidationError } from '../../../../modules/error';

/**
 * グループの所属メンバーを指定した集合に一括設定する（#2804）
 * - 現在の member_ids との差分（追加・除外）を計算
 * - group.member_ids と各ユーザーの users.group_ids を同一バッチで双方向更新
 * - 追加対象が所属上限(10)に達している場合はエラー（何も変更しない）
 */
export const setGroupMembers = async (
  companyId: string,
  groupId: string,
  memberIds: string[],
  updatedBy: string,
): Promise<void> => {
  const groupDocRef = doc(groupsRef(companyId), groupId);
  const snapshot = await getDoc(groupDocRef);
  if (!snapshot.exists()) {
    throw new Error('グループが見つかりません');
  }
  const current: string[] = snapshot.data().member_ids ?? [];
  const added = memberIds.filter(id => !current.includes(id));
  const removed = current.filter(id => !memberIds.includes(id));

  // 追加対象の所属上限チェック
  if (added.length > 0) {
    const userSnapshots = await Promise.all(added.map(uid => getDoc(doc(usersRef, uid))));
    for (const userSnapshot of userSnapshots) {
      const currentGroupIds: string[] = userSnapshot.data()?.group_ids ?? [];
      if (
        !currentGroupIds.includes(groupId) &&
        currentGroupIds.length >= groupLimit.maxGroupsPerUser
      ) {
        throw new ValidationError(groupMessages.userLimitReached(groupLimit.maxGroupsPerUser));
      }
    }
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  batch.update(groupDocRef, {
    member_ids: memberIds,
    updated_at: now,
    updated_by: updatedBy,
  });

  added.forEach(uid => {
    batch.update(doc(usersRef, uid), { group_ids: arrayUnion(groupId) });
  });
  removed.forEach(uid => {
    batch.update(doc(usersRef, uid), { group_ids: arrayRemove(groupId) });
  });

  await batch.commit();
};

/**
 * 指定ユーザーの所属グループを指定した集合に一括設定する（#2804）
 * - setGroupMembers の逆方向。担当者編集画面からの所属変更に使用
 * - 現在の users.group_ids との差分（追加・除外）を計算
 * - users.group_ids と各グループの member_ids を同一バッチで双方向更新
 * - 所属上限(10)を超える場合はエラー（何も変更しない）
 */
export const setUserGroups = async (
  companyId: string,
  userId: string,
  groupIds: string[],
  updatedBy: string,
): Promise<void> => {
  if (groupIds.length > groupLimit.maxGroupsPerUser) {
    throw new ValidationError(groupMessages.userLimitReached(groupLimit.maxGroupsPerUser));
  }

  const userDocRef = doc(usersRef, userId);
  const userSnapshot = await getDoc(userDocRef);
  if (!userSnapshot.exists()) {
    throw new Error('ユーザーが見つかりません');
  }
  const current: string[] = userSnapshot.data().group_ids ?? [];
  const added = groupIds.filter(id => !current.includes(id));
  const removed = current.filter(id => !groupIds.includes(id));

  // 変更が無い場合は何もしない
  if (added.length === 0 && removed.length === 0) {
    return;
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  batch.update(userDocRef, { group_ids: groupIds });

  added.forEach(gid => {
    batch.update(doc(groupsRef(companyId), gid), {
      member_ids: arrayUnion(userId),
      updated_at: now,
      updated_by: updatedBy,
    });
  });
  removed.forEach(gid => {
    batch.update(doc(groupsRef(companyId), gid), {
      member_ids: arrayRemove(userId),
      updated_at: now,
      updated_by: updatedBy,
    });
  });

  await batch.commit();
};
